import { useMedia } from '../ui/responsive';
import { Dialog, ConfirmDialog } from '../ui/overlays';
import { useNotice } from '../ui/notifications';
import { useEditorUI } from '../board/ui';
import { SelectionToolbar } from '../board/SelectionToolbar';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DEMO_BOARD_NAME, slugify, type AssetInfo, type BoardSummary, type Point } from '@scribbleplot/shared';
import { ApiError, api } from '../lib/api';
import { downloadJson } from '../lib/download';
import { measureImage, readImageAsDataUri } from '../lib/localImage';
import { initialsOf, useAccent, usePref, useSession } from '../lib/session';
import type { Navigate } from '../lib/router';
import { color } from '../tokens';
import { Spinner, Toast } from '../ui/primitives';
import { Canvas } from '../board/Canvas';
import { ContextMenu } from '../board/ContextMenu';
import { Minimap } from '../board/Minimap';
import { Sidebar } from '../board/Sidebar';
import { AssetsPanel, PLACEHOLDERS } from '../board/AssetsPanel';
import { Toolbar } from '../board/Toolbar';
import { ShortcutsPanel, ZoomBar } from '../board/ZoomBar';
import { OnboardingPanel } from '../board/OnboardingPanel';
import { useOnboarding } from '../board/onboarding';
import { DemoBanner } from '../board/DemoBanner';
import { carrySandbox, clearSandbox, loadSandbox } from '../board/sandbox';
import { TopBar } from '../board/TopBar';
import { useCamera } from '../board/camera';
import { useBoard, type Tool } from '../board/store';

/**
 * The board screen.
 *
 * Owns everything that is not the canvas itself: chrome, panels, keyboard
 * routing, uploads, and the load/attach lifecycle. Document state lives in the
 * board store so the canvas and the panels never pass it between themselves.
 */

const TOOL_KEYS: Record<string, Tool> = {
  v: 'select',
  h: 'hand',
  n: 'sticky',
  t: 'text',
  f: 'frame',
  d: 'draw',
  c: 'connect',
  l: 'link',
  m: 'comment',
};

export type BoardScreenProps = {
  /** Board id for an owned board, or a slug when arriving via a public link. */
  target: { kind: 'id'; id: string } | { kind: 'slug'; slug: string } | { kind: 'demo' };
  navigate: Navigate;
};

export function BoardScreen({ target, navigate }: BoardScreenProps) {
  const accent = useAccent();
  const dotGrid = usePref('dotGrid', true);
  const showMinimap = usePref('showMinimap', true);
  const presence = usePref('presence', true);
  const user = useSession((s) => s.user);

  const board = useBoard();
  /**
   * Subscribed here, not in the JSX below: this component has early returns for
   * the loading and error states, and a hook called after them would change the
   * hook count between renders.
   */
  const scale = useCamera((s) => s.cam.s);

  const [loadAttempt, setLoadAttempt] = useState(0);
  const [loadRetryable, setLoadRetryable] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { notice: toast, notify } = useNotice();
  const desktop = useMedia('(min-width: 1200px)');
  const phone = useMedia('(max-width: 767px)');
  const ui = useEditorUI();
  const [confirmReset, setConfirmReset] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [assetError, setAssetError] = useState<string | null>(null);
  const [boards, setBoards] = useState<BoardSummary[]>([]);
  const [assets, setAssets] = useState<AssetInfo[]>([]);
  const [uploading, setUploading] = useState(false);
  const sidebarOpen = desktop ? ui.layers : ui.overlay === 'layers';
  const assetsOpen = desktop ? ui.assets : ui.overlay === 'assets';
  const setSidebarOpen = (value: boolean | ((v: boolean) => boolean)) => {
    const next = typeof value === 'function' ? value(sidebarOpen) : value;
    if (desktop) {
      if (next !== ui.layers) ui.togglePanel('layers');
    } else ui.open(next ? 'layers' : null);
  };
  const setAssetsOpen = (value: boolean | ((v: boolean) => boolean)) => {
    const next = typeof value === 'function' ? value(assetsOpen) : value;
    if (desktop) {
      if (next !== ui.assets) ui.togglePanel('assets');
    } else ui.open(next ? 'assets' : null);
  };
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [ghost, setGhost] = useState<{ x: number; y: number; src: string } | null>(null);

  const uploadLock = useRef(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  /* ---------------- load & attach ---------------- */

  const key = target.kind === 'demo' ? 'demo' : target.kind === 'id' ? target.id : target.slug;
  const isDemo = target.kind === 'demo';

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);

    (async () => {
      // The demo never touches the server: it opens whatever is in this
      // browser, or the worked example if there is nothing yet.
      if (target.kind === 'demo') {
        const doc = loadSandbox();
        useBoard.getState().attachSandbox(DEMO_BOARD_NAME, doc);
        requestAnimationFrame(() => {
          const frame = doc.items.find((item) => item.type === 'frame');
          useCamera.getState().fit(phone && frame ? { items: [frame], strokes: [] } : doc);
        });
        return;
      }

      try {
        const result = target.kind === 'id' ? await api.getBoard(target.id) : await api.getPublicBoard(target.slug);
        if (cancelled) return;
        const { board: detail, canEdit, canManage } = result;
        useBoard.getState().attach({
          boardId: detail.id,
          slug: detail.slug,
          name: detail.name,
          isPublic: detail.isPublic,
          canEdit,
          canManage,
          role: detail.role,
          doc: detail.doc,
          rev: detail.rev,
        });
        useBoard.getState().connect();

        // Frame the content on first open; a saved camera takes precedence.
        const hadSavedCamera = localStorage.getItem(`sp.cam.${detail.id}`);
        if (!hadSavedCamera) {
          requestAnimationFrame(() => useCamera.getState().fit(detail.doc));
        }
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof ApiError ? err.message : 'Could not open this board.');
        setLoadRetryable(!(err instanceof ApiError) || err.status >= 500);
      }
    })();

    return () => {
      cancelled = true;
      useBoard.getState().detach();
      useEditorUI.setState({ overlay: null, multiSelect: false });
      useOnboarding.getState().dismiss();
      onboardingChecked.current = false;
    };
  }, [key, target.kind, loadAttempt]);

  /**
   * Offer the checklist on an empty board, once per account.
   *
   * Latched on open rather than tracked live: the board stops being empty the
   * moment the first step is done, and the panel should stay to show the rest.
   */
  const onboardingChecked = useRef(false);
  useEffect(() => {
    if (onboardingChecked.current || !board.boardId || !user) return;
    onboardingChecked.current = true;
    const fresh = !user.prefs?.onboarded;
    if (fresh && board.canEdit && !board.doc.items.length && !board.doc.strokes.length) {
      useOnboarding.getState().start();
    }
  }, [board.boardId, board.canEdit, board.doc, user]);

  /** Remember that the ropes are learned, so it never comes back. */
  const settleOnboarding = useCallback(
    (message?: string) => {
      useOnboarding.getState().dismiss();
      if (message) notify(message);
      void useSession
        .getState()
        .updatePrefs({ onboarded: true })
        .catch(() => undefined);
    },
    [notify],
  );

  /** The sidebar's board list and the asset library are owner-only. */
  useEffect(() => {
    if (!user || isDemo) return;
    api
      .listBoards()
      .then(({ boards: list }) => setBoards(list))
      .catch(() => undefined);
    api
      .listAssets()
      .then(({ assets: list }) => {
        setAssets(list);
        setAssetError(null);
      })
      .catch(() => setAssetError('Could not load your assets.'));
  }, [user]);

  /* ---------------- server-side errors ---------------- */

  useEffect(() => {
    if (board.lastError) notify({ kind: 'error', message: board.lastError });
  }, [board.lastError, notify]);

  /* ---------------- uploads ---------------- */

  const uploadFiles = useCallback(
    async (files: File[], at?: Point) => {
      if (!files.length || !board.canEdit || !board.boardId) return;

      if (uploadLock.current) return;
      uploadLock.current = true;
      setUploading(true);
      const targetId = board.boardId;
      const failures: { file: File; reason: string }[] = [];
      let completed = 0;
      const viewport = useCamera.getState().viewport?.getBoundingClientRect();
      const center = viewport
        ? useCamera.getState().toWorld(viewport.left + viewport.width / 2, viewport.top + viewport.height / 2)
        : [0, 0];
      const placeAt = at ?? (phone || isDemo ? center : undefined);
      try {
        for (const [index, file] of files.entries()) {
          setUploadProgress(`Adding images · ${completed} of ${files.length} complete`);
          try {
            if (isDemo) {
              const src = await readImageAsDataUri(file);
              const size = await measureImage(src);
              if (useBoard.getState().boardId !== targetId) break;
              useBoard
                .getState()
                .addImage(
                  src,
                  size.w,
                  size.h,
                  (placeAt?.[0] ?? 0) + index * 36,
                  (placeAt?.[1] ?? 0) + index * 36,
                  file.name.replace(/\.[^.]+$/, ''),
                );
            } else {
              const created = await api.uploadAssets([file], targetId);
              if (useBoard.getState().boardId !== targetId) break;
              setAssets((current) => [...created, ...current]);
              if (placeAt)
                created.forEach((asset) =>
                  useBoard
                    .getState()
                    .addImage(
                      asset.url,
                      asset.w,
                      asset.h,
                      placeAt[0] + index * 36,
                      placeAt[1] + index * 36,
                      asset.name,
                      asset.id,
                    ),
                );
            }
            completed++;
          } catch (err) {
            failures.push({ file, reason: err instanceof Error ? err.message : 'Could not read this image' });
          }
        }
        if (useBoard.getState().boardId !== targetId) return;
        if (failures.length)
          notify({
            kind: 'error',
            message: `${completed} of ${files.length} images added. ${failures.map(({ file, reason }) => `${file.name}: ${reason}`).join('; ')}`,
            action: {
              label: 'Retry failed',
              run: () =>
                void uploadFiles(
                  failures.map((f) => f.file),
                  at,
                ),
            },
          });
        else
          notify({
            kind: 'success',
            message: placeAt
              ? `Added ${completed} image${completed === 1 ? '' : 's'}`
              : 'Added to assets — drag an image onto the canvas',
          });
        if (!placeAt && completed) {
          if (desktop) {
            if (!useEditorUI.getState().assets) useEditorUI.getState().togglePanel('assets');
          } else useEditorUI.getState().open('assets');
        }
      } finally {
        uploadLock.current = false;
        setUploading(false);
        setUploadProgress(null);
      }
    },
    [board.canEdit, board.boardId, isDemo, notify, phone, desktop],
  );

  const onFilePicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = [...(event.target.files ?? [])];
    event.target.value = '';
    void uploadFiles(files);
  };

  const deleteAsset = async (id: string) => {
    try {
      await api.deleteAsset(id);
      setAssets((current) => current.filter((asset) => asset.id !== id));
      notify('Removed from assets');
    } catch (err) {
      notify({ kind: 'error', message: err instanceof ApiError ? err.message : 'Could not remove that asset.' });
    }
  };

  /* ---------------- keyboard ---------------- */

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      // Never hijack keys while the user is typing.
      if (useEditorUI.getState().overlay || document.querySelector('[role=dialog]')) return;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;

      const store = useBoard.getState();
      const mod = event.metaKey || event.ctrlKey;

      if (mod) {
        const key = event.key.toLowerCase();
        if (key === 'z') {
          event.preventDefault();
          if (event.shiftKey) store.redo();
          else store.undo();
          return;
        }
        if (key === 'y') {
          event.preventDefault();
          store.redo();
          return;
        }
        if (key === 'g') {
          event.preventDefault();
          if (event.shiftKey) {
            const group = store.sel.map((id) => store.itemById(id)).find((item) => item?.type === 'group');
            if (group) store.ungroup(group.id);
          } else if (!store.groupSel()) {
            notify('Select 2+ items to group');
          }
          return;
        }
        if (key === 'd') {
          event.preventDefault();
          store.duplicateSel();
          return;
        }
        if (key === 'a') {
          event.preventDefault();
          store.selectAll();
          return;
        }
        if (key === '=' || key === '+') {
          event.preventDefault();
          useCamera.getState().zoomBy(1.25);
          return;
        }
        if (key === '-') {
          event.preventDefault();
          useCamera.getState().zoomBy(0.8);
          return;
        }
        if (key === '0') {
          event.preventDefault();
          useCamera.getState().reset();
          return;
        }
        if (key === '1') {
          event.preventDefault();
          useCamera.getState().fit(store.doc);
          return;
        }
        return;
      }

      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (!store.sel.length) return;
        event.preventDefault();
        store.deleteSel();
        return;
      }

      if (event.key === 'Escape') {
        // Dismiss transient UI first; only clear the selection if nothing was open.
        const hadOverlay = store.menu || store.connectFrom || store.editing;
        store.clearOverlays();
        store.setTool('select');
        if (!hadOverlay) store.setSel([]);
        setShortcutsOpen(false);
        useEditorUI.setState({ multiSelect: false, overlay: null });
        return;
      }

      if (event.key === '?') {
        setShortcutsOpen((open) => !open);
        return;
      }

      const tool = TOOL_KEYS[event.key.toLowerCase()];
      if (tool) store.setTool(tool);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [notify]);

  /* ---------------- actions ---------------- */

  const focusItem = (id: string) => {
    const item = useBoard.getState().itemById(id);
    if (!item) return;
    useBoard.getState().setSel([id]);
    useCamera.getState().centerOn(item);
  };

  const togglePublic = async (isPublic: boolean) => {
    if (!board.boardId) return;
    try {
      await api.updateBoard(board.boardId, { isPublic });
      useBoard.setState({ isPublic });
      notify(isPublic ? 'Public link is on' : 'Public link is off');
    } catch (err) {
      notify({ kind: 'error', message: err instanceof ApiError ? err.message : 'Could not change sharing.' });
    }
  };

  const exportBoard = async () => {
    if (!board.boardId) return;

    if (isDemo) {
      // Built here rather than fetched: there is no board on the server to ask
      // for. Images are already data URIs, so they travel in the document.
      downloadJson('demo.scribbleplot.json', {
        scribbleplot: 1,
        name: board.name,
        exportedAt: new Date().toISOString(),
        doc: board.doc,
        assets: [],
      });
      notify('Board exported');
      return;
    }

    try {
      const file = await api.exportBoard(board.boardId);
      // Named from the current title, not the slug: the slug is fixed when the
      // board is created, so a renamed board would export under its old name.
      downloadJson(`${slugify(board.name || 'board')}.scribbleplot.json`, file);
      notify('Board exported');
    } catch (err) {
      notify({ kind: 'error', message: err instanceof ApiError ? err.message : 'Could not export this board.' });
    }
  };

  const shareUrl = `${location.origin}/b/${board.slug || slugify(board.name || 'board')}`;

  /** Copy any text to the clipboard, with a caller-supplied confirmation. */
  const copyText = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      notify(label);
    } catch {
      // Clipboard access can be refused; the link is on screen to copy by hand.
      notify({
        kind: 'error',
        message: 'Copy failed — select the link and copy it manually.',
        action: { label: 'Retry', run: () => void copyText(text, label) },
      });
    }
  };

  /* ---------------- render ---------------- */

  if (loadError) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <div role="alert" style={{ fontSize: 17, fontWeight: 700 }}>
          {loadError}
        </div>
        {loadRetryable && (
          <button className="sp-button" onClick={() => setLoadAttempt((n) => n + 1)}>
            Retry
          </button>
        )}
        <button
          type="button"
          onClick={() => navigate(user ? { name: 'home' } : { name: 'landing' })}
          style={{
            height: 34,
            padding: '0 16px',
            borderRadius: 9,
            border: `1px solid ${color.lineStrong}`,
            background: 'transparent',
            color: color.text,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {user ? 'Back to boards' : 'Go to scribbleplot.com'}
        </button>
      </div>
    );
  }

  if (!board.boardId) return <Spinner accent={accent} label="Opening board" />;

  const authorName = user?.name ?? 'Guest';

  return (
    <div className="sp-board-shell" style={{ '--sp-accent': accent } as React.CSSProperties}>
      <TopBar
        name={board.name}
        accent={accent}
        boardId={board.boardId}
        canEdit={board.canEdit}
        canManage={board.canManage}
        role={board.role}
        status={board.status}
        peerCount={board.peers.length}
        sidebarOpen={sidebarOpen}
        assetsOpen={assetsOpen}
        items={board.doc.items}
        isPublic={board.isPublic}
        shareUrl={shareUrl}
        initials={user ? initialsOf(user.name) : ''}
        onBack={() => navigate(user ? { name: 'home' } : { name: 'landing' })}
        onRename={(next) => board.renameBoard(next)}
        onToggleSidebar={() => setSidebarOpen((open) => !open)}
        onToggleAssets={() => setAssetsOpen((open) => !open)}
        onTogglePublic={togglePublic}
        onFocusItem={focusItem}
        onOpenSettings={() => navigate(user ? { name: 'settings' } : { name: 'signup' })}
        onExport={() => void exportBoard()}
        onCopy={(text, label) => void copyText(text, label)}
        onNotify={notify}
      />

      <div className="sp-board-row">
        {sidebarOpen && (
          <ResponsivePanel title="Layers" desktop={desktop} onClose={() => setSidebarOpen(false)}>
            <Sidebar
              boards={boards}
              currentBoardId={board.boardId}
              items={board.doc.items}
              sel={board.sel}
              accent={accent}
              onOpenBoard={(id) => navigate({ name: 'board', id })}
              onFocusItem={(id) => {
                focusItem(id);
                if (!desktop) ui.open(null);
              }}
            />
          </ResponsivePanel>
        )}

        {/*
          The floating controls are positioned against this wrapper, not the
          whole row, so they stay inside the canvas region instead of sliding
          under the side panels when those are open.
        */}
        <div className="sp-canvas-region">
          <Canvas
            accent={accent}
            dotGrid={dotGrid}
            presence={presence}
            authorName={authorName}
            onUploadFiles={(files, at) => void uploadFiles(files, at)}
            onGhostChange={setGhost}
          />

          <Toolbar
            tool={board.tool}
            accent={accent}
            disabled={!board.canEdit}
            onPick={(tool) => {
              board.setTool(tool);
              if (tool !== 'select') useEditorUI.setState({ multiSelect: false });
              if (tool === 'connect') notify('Click two items to connect them');
              if (tool === 'comment') notify('Click anywhere to leave a comment');
            }}
            onPickImage={() => fileInputRef.current?.click()}
          />

          <SelectionToolbar accent={accent} />
          {uploadProgress && (
            <div role="status" className="sp-upload-status">
              {uploadProgress}
            </div>
          )}
          {(board.tool === 'connect' || board.tool === 'comment' || ui.multiSelect) && (
            <div className="sp-tool-hint" style={isDemo && !phone ? { top: 76 } : undefined}>
              {ui.multiSelect
                ? 'Tap items to add or remove them from your selection'
                : board.tool === 'connect'
                  ? board.connectFrom
                    ? 'Now choose the item to connect to'
                    : 'Choose the first item to connect'
                  : 'Choose a point for your comment'}
            </div>
          )}
          {isDemo && (
            <DemoBanner
              accent={accent}
              onKeep={() => {
                // Park the work so signing up can bring it along.
                carrySandbox(board.doc);
                navigate({ name: 'signup' });
              }}
              onReset={() => setConfirmReset(true)}
            />
          )}

          <OnboardingPanel
            accent={accent}
            onComplete={() => settleOnboarding('Nice — you know the ropes')}
            onDismiss={() => settleOnboarding()}
          />

          {showMinimap && !phone && <Minimap doc={board.doc} accent={accent} />}
          {shortcutsOpen && (
            <ShortcutsPanel
              minimapVisible={showMinimap && !phone}
              onShowGuide={() => {
                useOnboarding.getState().start();
                setShortcutsOpen(false);
              }}
            />
          )}

          <ZoomBar
            scale={scale}
            accent={accent}
            shortcutsOpen={shortcutsOpen}
            onZoomIn={() => useCamera.getState().zoomBy(1.25)}
            onZoomOut={() => useCamera.getState().zoomBy(0.8)}
            onFit={() => useCamera.getState().fit(board.doc)}
            onToggleShortcuts={() => setShortcutsOpen((open) => !open)}
          />
        </div>

        {assetsOpen && (
          <ResponsivePanel title="Assets" desktop={desktop} onClose={() => setAssetsOpen(false)}>
            {assetError && (
              <div role="alert" className="sp-error">
                {assetError}
                <button
                  className="sp-button"
                  onClick={() => {
                    void api
                      .listAssets()
                      .then(({ assets }) => {
                        setAssets(assets);
                        setAssetError(null);
                      })
                      .catch(() => setAssetError('Could not load your assets.'));
                  }}
                >
                  Retry
                </button>
              </div>
            )}
            <AssetsPanel
              assets={assets}
              placeholders={PLACEHOLDERS}
              accent={accent}
              canEdit={board.canEdit}
              uploading={uploading}
              unavailable={!!assetError}
              onPickImage={() => fileInputRef.current?.click()}
              onDeleteAsset={(id) => void deleteAsset(id)}
            />
          </ResponsivePanel>
        )}
      </div>

      {board.menu && <ContextMenu menu={board.menu} accent={accent} />}

      {/* Follows the pointer during an asset drag, above every panel. */}
      {ghost && (
        <div
          aria-hidden
          style={{
            position: 'fixed',
            left: ghost.x - 56,
            top: ghost.y - 42,
            width: 112,
            height: 84,
            zIndex: 10_001,
            pointerEvents: 'none',
            opacity: 0.85,
            borderRadius: 10,
            backgroundImage: `url("${ghost.src}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            boxShadow: `0 0 0 1.5px ${accent}, 0 16px 40px rgba(0,0,0,.5)`,
          }}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={onFilePicked}
        style={{ display: 'none' }}
      />

      {confirmReset && (
        <ConfirmDialog
          title="Reset demo board?"
          description="This replaces your browser's demo with the original example. Your current demo edits will be lost."
          confirmLabel="Reset demo"
          onClose={() => setConfirmReset(false)}
          onConfirm={() => {
            clearSandbox();
            const doc = loadSandbox();
            useBoard.getState().attachSandbox(DEMO_BOARD_NAME, doc);
            useCamera.getState().fit(doc);
            notify('Demo reset');
          }}
        />
      )}
      <Toast message={toast} />
    </div>
  );
}
function ResponsivePanel({
  title,
  desktop,
  onClose,
  children,
}: {
  title: string;
  desktop: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return desktop ? (
    <>{children}</>
  ) : (
    <Dialog title={title} drawer onClose={onClose}>
      <div className="sp-editor-drawer-content">{children}</div>
    </Dialog>
  );
}
